import { useState, useEffect, useRef } from "react";
import { ChevronRight, ChevronDown, LogOut, Menu } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { useEmpresa } from "../context/EmpresaContext";

const PAGE_LABELS = {
  dashboard:       "Dashboard",
  conciliacao:     "Conciliação",
  batches:         "Batches de Pagamento",
  arquivo:         "Arquivo Drive",
  efatura:         "E-Fatura AT",
  estatisticas:    "Análise",
  empresas:        "Empresas",
  manutencao:      "Manutenção",
  agente:          "Agente",
  utilizadores:    "Equipa",
  definicoes:      "Definições",
  "despesa-rapida": "Despesa Rápida",
  fundos:          "Fundos de Projeto",
  socios:          "Sócios",
  "processamento-assistido": "Processamento Assistido",
  "emails-pendentes": "Documentos Pendentes",
  sistema:         "Sistema",
};

const SIDEBAR_PREFERENCE_KEY = "accounting-sidebar-collapsed";

function initialSidebarCollapsed() {
  if (typeof window === "undefined") return false;
  const saved = window.localStorage.getItem(SIDEBAR_PREFERENCE_KEY);
  if (saved !== null) return saved === "true";
  return window.matchMedia("(max-width: 1280px)").matches;
}

// Trocador rápido de empresa no breadcrumb -- não precisa de sair da página
// nem de voltar ao ecrã de escolha pós-login (esse continua a ser o ponto de
// entrada obrigatório por sessão); isto é só para quando já se está a
// trabalhar e se quer mudar de empresa sem perder o sítio onde se está.
// "Seguro" no sentido em que muda através do mesmo useEmpresa() que todas as
// páginas já leem -- não há forma de ficar com uma página a olhar para uma
// empresa e outra para outra.
function TrocarEmpresa() {
  const { empresa, empresas, setEmpresa } = useEmpresa();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const atual = (empresas || []).find((e) => e.nif === empresa);
  if (!empresas || empresas.length === 0) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 4, fontSize: 12,
          color: "#344054", background: open ? "#F2F4F7" : "none",
          border: "none", borderRadius: 6, padding: "4px 6px", cursor: "pointer", whiteSpace: "nowrap",
          maxWidth: 160,
        }}
        className="layout-breadcrumb-company"
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{atual ? atual.nome : "Selecionar empresa"}</span>
        <ChevronDown size={12} style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 240, maxHeight: 320, overflowY: "auto",
          background: "#fff", border: "1px solid #D8DDE6", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,.25)", zIndex: 999,
        }}>
          {empresas.map((e) => (
            <button
              key={e.nif}
              onClick={() => { setEmpresa(e.nif); setOpen(false); }}
              style={{
                width: "100%", padding: "9px 14px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1,
                background: empresa === e.nif ? "#EEF1F6" : "#fff", border: "none", borderBottom: "1px solid #EEF1F6",
                cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1A1F2E" }}>{e.nome}</span>
              <span style={{ fontSize: 10, color: "#9AA3B0", fontFamily: "ui-monospace, monospace" }}>NIF {e.nif}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Layout({ page, setPage, user, onLogout, alertCount = 0, searchSlot, notifSlot, showAdmin = true, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebarCollapsed);
  const drawerRef = useRef(null);

  const initials = user
    ? (user.nome || user.email || "?")[0].toUpperCase()
    : "?";

  // Close mobile drawer on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  // Close drawer on page change (mobile)
  useEffect(() => { setMobileOpen(false); }, [page]);

  const changeSidebarCollapsed = (next) => {
    setSidebarCollapsed(next);
    window.localStorage.setItem(SIDEBAR_PREFERENCE_KEY, String(next));
  };

  return (
    <div className="accounting-shell" style={{ display: "flex", height: "100dvh", background: "#F4F7F9", overflow: "hidden", fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif" }}>

      {/* ── Sidebar — desktop (hidden on mobile) ── */}
      <div className="layout-sidebar-desktop">
        <Sidebar
          active={page}
          onChange={setPage}
          alertCount={alertCount}
          showAdmin={showAdmin}
          collapsed={sidebarCollapsed}
          onCollapsedChange={changeSidebarCollapsed}
        />
      </div>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 200,
          }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Mobile drawer ── */}
      <div
        ref={drawerRef}
        style={{
          position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 210,
          transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
          display: "flex", flexDirection: "column",
          background: "#101828", width: 252, height: "100%",
          boxShadow: mobileOpen ? "4px 0 24px rgba(0,0,0,0.15)" : "none",
        }}
        className="layout-sidebar-mobile"
      >
        <Sidebar
          active={page}
          onChange={(id) => { setPage(id); setMobileOpen(false); }}
          alertCount={alertCount}
          showAdmin={showAdmin}
          collapsed={false}
          collapsible={false}
        />
      </div>

      {/* ── Main column ── */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, overflow: "hidden" }}>

        {/* ── Topbar ── */}
        <header className="accounting-topbar" style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "0 28px", height: 64, flexShrink: 0,
          background: "rgba(255,255,255,.96)", borderBottom: "1px solid #E4E7EC",
          zIndex: 100,
        }}>
          {/* Mobile hamburger */}
          <button
            className="layout-hamburger"
            aria-label="Abrir menu"
            onClick={() => setMobileOpen(true)}
            style={{
              display: "none", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, border: "1px solid #E4E7EC", background: "#fff",
              borderRadius: 9, cursor: "pointer", flexShrink: 0, color: "#344054",
            }}
          >
            <Menu size={18} />
          </button>

          {/* Breadcrumb */}
          <nav style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 12, color: "#98A2B3", whiteSpace: "nowrap" }} className="layout-breadcrumb-root">
              Hub Financeiro
            </span>
            <ChevronRight size={12} color="#D0D5DD" style={{ flexShrink: 0 }} className="layout-breadcrumb-root" />
            <TrocarEmpresa />
            <ChevronRight size={12} color="#D0D5DD" style={{ flexShrink: 0 }} className="layout-breadcrumb-company-separator" />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#101828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {PAGE_LABELS[page] || page}
            </span>
          </nav>

          {/* Right actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <div className="layout-search-slot">{searchSlot}</div>
            {notifSlot}

            {user && (
              <>
                <div style={{ width: 1, height: 22, background: "#E4E7EC", margin: "0 4px" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: "#E6F8F5", border: "1px solid #B9E9E1",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800, color: "#0F766E", flexShrink: 0,
                  }}>
                    {initials}
                  </div>
                  <div className="layout-user-name" style={{ lineHeight: 1.3 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: "#101828", margin: 0, whiteSpace: "nowrap" }}>
                      {user.nome || user.email}
                    </p>
                    {user.nome && (
                      <p style={{ fontSize: 10, color: "#98A2B3", margin: 0, whiteSpace: "nowrap" }}>
                        {user.email}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={onLogout}
                    title="Terminar sessão"
                    style={{
                      padding: 7, borderRadius: 6,
                      background: "#fff", border: "1px solid #E4E7EC", cursor: "pointer",
                      color: "#667085", display: "flex", alignItems: "center",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#F2F4F7"; e.currentTarget.style.color = "#101828"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#667085"; }}
                  >
                    <LogOut size={14} />
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* ── Page content ── */}
        <main className="accounting-content" style={{ flex: 1, overflowY: "auto", background: "#F4F7F9" }}>
          {children}
        </main>
      </div>

      <style>{`
        /* Desktop sidebar visible, mobile hamburger hidden */
        .layout-sidebar-desktop { display: flex; }
        .layout-sidebar-mobile  { display: flex; }
        .layout-hamburger       { display: none !important; }
        .layout-breadcrumb-root { display: inline !important; }
        .layout-breadcrumb-company { display: flex !important; }
        .layout-breadcrumb-company-separator { display: block !important; }
        .layout-user-name       { display: block; }
        .layout-search-slot     { display: block; }

        @media (max-width: 960px) {
          .layout-sidebar-desktop { display: none !important; }
          .layout-hamburger       { display: flex !important; }
          .layout-breadcrumb-root { display: none !important; }
          .layout-user-name       { display: none; }
          .layout-search-slot     { display: none; }
        }

        @media (max-width: 768px) {
          .layout-breadcrumb-company,
          .layout-breadcrumb-company-separator { display: none !important; }
        }
      `}</style>
    </div>
  );
}
